/**
 * 중복 레포트 방지 시스템
 * - 동일 레포트 중복 업로드 방지
 * - 서버측 레포트 존재 여부 확인
 * - 간단한 충돌 해결 로직
 */

class DuplicateChecker {
  constructor() {
    this.processedReports = new Set(); // 현재 세션에서 처리된 레포트 ID들
    this.checkCache = new Map(); // 서버 존재 여부 캐시 (5분)
    this.cacheTimeout = 5 * 60 * 1000; // 5분
  }

  /**
   * 레포트 중복 처리 여부 확인
   */
  isAlreadyProcessed(reportId) {
    return this.processedReports.has(reportId);
  }

  /**
   * 처리된 레포트로 마킹
   */
  markAsProcessed(reportId) {
    this.processedReports.add(reportId);
    // console.log('[DuplicateChecker] 레포트 처리 완료 마킹:', reportId);
  }

  /**
   * 서버에 레포트가 이미 존재하는지 확인
   */
  async checkServerDuplicate(reportId, apiGetFunction) {
    // 오프라인 ID는 서버에 존재할 수 없음
    if (reportId.startsWith('offline_')) {
      return { exists: false, isOfflineId: true };
    }

    // 캐시 확인
    const cacheKey = `server_${reportId}`;
    const cached = this.checkCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp < this.cacheTimeout)) {
      // console.log('[DuplicateChecker] 캐시된 결과 사용:', reportId, cached.result);
      return cached.result;
    }

    try {
      // console.log('[DuplicateChecker] 서버 중복 확인:', reportId);
      const response = await apiGetFunction(`/reports/${reportId}`);

      const result = {
        exists: !!response.data,
        serverData: response.data || null,
        lastModified: response.data?.updatedAt || response.data?.createdAt
      };

      // 결과 캐싱
      this.checkCache.set(cacheKey, {
        result,
        timestamp: Date.now()
      });

      // console.log('[DuplicateChecker] 서버 확인 결과:', reportId, result.exists ? '존재함' : '없음');
      return result;

    } catch (error) {
      // 404는 존재하지 않음을 의미
      if (error.response && error.response.status === 404) {
        const result = { exists: false };
        this.checkCache.set(cacheKey, {
          result,
          timestamp: Date.now()
        });
        return result;
      }

      // console.error('[DuplicateChecker] 서버 확인 실패:', error);
      // 에러 시에는 업로드 시도 (안전한 방향으로)
      return { exists: false, error: error.message };
    }
  }

  /**
   * 충돌 해결 로직
   */
  resolveConflict(localReport, serverData) {
    // console.log('[DuplicateChecker] 충돌 감지, 해결 시도:', localReport.id);

    // 간단한 해결 전략: 타임스탬프 비교
    const localTimestamp = new Date(localReport.offlineMetadata?.savedAt || localReport.updatedAt);
    const serverTimestamp = new Date(serverData.lastModified);

    if (localTimestamp > serverTimestamp) {
      // console.log('[DuplicateChecker] 로컬 데이터가 더 최신, 업데이트 수행');
      return {
        action: 'update',
        reason: 'local_newer',
        localTime: localTimestamp.toISOString(),
        serverTime: serverTimestamp.toISOString()
      };
    } else {
      // console.log('[DuplicateChecker] 서버 데이터가 더 최신, 업로드 스킵');
      return {
        action: 'skip',
        reason: 'server_newer',
        localTime: localTimestamp.toISOString(),
        serverTime: serverTimestamp.toISOString()
      };
    }
  }

  /**
   * 레포트 업로드 전 종합 체크
   */
  async checkBeforeUpload(report, apiGetFunction) {
    const reportId = report.id;

    // console.log('[DuplicateChecker] 업로드 전 종합 체크 시작:', reportId);

    // 1. 현재 세션에서 이미 처리했는지 확인
    if (this.isAlreadyProcessed(reportId)) {
      return {
        shouldUpload: false,
        reason: 'already_processed_in_session',
        message: '현재 세션에서 이미 처리된 레포트입니다.'
      };
    }

    // 2. 서버 중복 확인
    const serverCheck = await this.checkServerDuplicate(reportId, apiGetFunction);

    if (!serverCheck.exists) {
      // 서버에 없으면 업로드 진행
      return {
        shouldUpload: true,
        isNew: serverCheck.isOfflineId,
        reason: 'not_exists_on_server'
      };
    }

    // 3. 서버에 존재하는 경우 충돌 해결
    const resolution = this.resolveConflict(report, serverCheck);

    if (resolution.action === 'update') {
      return {
        shouldUpload: true,
        isUpdate: true,
        reason: 'conflict_resolved_update',
        resolution
      };
    } else {
      return {
        shouldUpload: false,
        reason: 'conflict_resolved_skip',
        resolution,
        message: '서버에 더 최신 버전이 존재하여 업로드를 건너뜁니다.'
      };
    }
  }

  /**
   * 캐시 정리
   */
  clearCache() {
    this.checkCache.clear();
    // console.log('[DuplicateChecker] 캐시 정리 완료');
  }

  /**
   * 세션 초기화
   */
  resetSession() {
    this.processedReports.clear();
    this.clearCache();
    // console.log('[DuplicateChecker] 세션 초기화 완료');
  }

  /**
   * 통계 정보 반환
   */
  getStats() {
    return {
      processedCount: this.processedReports.size,
      cacheSize: this.checkCache.size,
      processedReports: Array.from(this.processedReports),
      cacheKeys: Array.from(this.checkCache.keys())
    };
  }
}

// 싱글톤 인스턴스
const duplicateChecker = new DuplicateChecker();

export const checkBeforeUpload = (report, apiGetFunction) => duplicateChecker.checkBeforeUpload(report, apiGetFunction);
export const markAsProcessed = (reportId) => duplicateChecker.markAsProcessed(reportId);
export const isAlreadyProcessed = (reportId) => duplicateChecker.isAlreadyProcessed(reportId);
export const clearCache = () => duplicateChecker.clearCache();
export const resetSession = () => duplicateChecker.resetSession();
export const getDuplicateStats = () => duplicateChecker.getStats();

export default duplicateChecker;